import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, MapPin, Stethoscope, Inbox, AlertTriangle, Mail, Building2, Sparkles, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import LocationFormDialog from "@/components/provider/LocationFormDialog";
import StaffFormDialog from "@/components/provider/StaffFormDialog";
import OfferFormDialog from "@/components/offers/OfferFormDialog";
import EditProviderDialog from "@/components/admin/EditProviderDialog";
import { adminProvidersApi } from "@/lib/api/adminProviders";
import { adminOffersApi } from "@/lib/api/offers";
import { categoriesApi } from "@/lib/api/categories";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

function KycBadge({ status, t }) {
    const tone = status === "APPROVED" ? "border-success/40 text-success bg-success/5"
              : status === "PENDING"  ? "border-warning/40 text-warning bg-warning/5"
              : status === "REJECTED" ? "border-destructive/40 text-destructive bg-destructive/5"
              : "border-border text-muted-foreground";
    return <Badge variant="outline" className={tone}>{t(`admin.providers.kyc.${status}`)}</Badge>;
}

export default function ProviderDetailSheet({ open, onOpenChange, providerId, onProviderChanged }) {
    const { t, isRTL, lang } = useI18n();
    const [tab, setTab] = useState("overview");
    const [provider, setProvider] = useState(null);
    const [locations, setLocations] = useState(null);
    const [staff, setStaff] = useState(null);
    const [offers, setOffers] = useState(null);
    const [allL2Categories, setAllL2Categories] = useState([]);
    const [loading, setLoading] = useState(false);

    // Edit dialogs (overview / locations / staff / offers)
    const [editOpen, setEditOpen] = useState(false);
    const [locDialog, setLocDialog] = useState({ open: false, initial: null });
    const [staffDialog, setStaffDialog] = useState({ open: false, initial: null });
    const [offerDialog, setOfferDialog] = useState({ open: false, initial: null });
    const [confirmLocDel, setConfirmLocDel] = useState(null);
    const [confirmStaffDel, setConfirmStaffDel] = useState(null);
    const [decision, setDecision] = useState({ open: false, offer: null, action: null });

    const loadAll = async (pid) => {
        if (!pid) return;
        setLoading(true);
        try {
            const [p, locs, st, ofRes, tree] = await Promise.all([
                adminProvidersApi.get(pid),
                adminProvidersApi.listLocations(pid),
                adminProvidersApi.listStaff(pid),
                adminOffersApi.list({ provider_id: pid }),
                categoriesApi.listTree(false),
            ]);
            setProvider(p);
            setLocations(locs);
            setStaff(st);
            setOffers(ofRes.items || []);
            const flat = [];
            const seen = new Set();
            (tree || []).forEach((l1) => (l1.children || []).forEach((l2) => {
                if (!seen.has(l2.id)) { seen.add(l2.id); flat.push(l2); }
            }));
            setAllL2Categories(flat);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && providerId) {
            setTab("overview");
            setProvider(null); setLocations(null); setStaff(null); setOffers(null);
            loadAll(providerId);
        }
    }, [open, providerId]);

    const refreshAggregates = async () => {
        // After a location/staff change, refetch the provider doc so
        // branch_count / neighborhoods / linked-categories stay accurate.
        try {
            const fresh = await adminProvidersApi.get(providerId);
            setProvider(fresh);
            onProviderChanged?.(fresh);
        } catch (_) { /* ignore */ }
    };

    const saveLocation = ({ id, payload }) =>
        id
            ? adminProvidersApi.updateLocation(providerId, id, payload)
            : adminProvidersApi.createLocation(providerId, payload);

    const onLocationSaved = async () => {
        const list = await adminProvidersApi.listLocations(providerId);
        setLocations(list);
        refreshAggregates();
    };

    const onLocationDelete = async () => {
        try {
            await adminProvidersApi.deleteLocation(providerId, confirmLocDel.id);
            toast.success(t("provider.locations.deleted"));
            setConfirmLocDel(null);
            const [list, st] = await Promise.all([
                adminProvidersApi.listLocations(providerId),
                adminProvidersApi.listStaff(providerId),
            ]);
            setLocations(list);
            setStaff(st);  // backend strips matching availability slots
            refreshAggregates();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };

    const saveStaff = ({ id, payload }) =>
        id
            ? adminProvidersApi.updateStaff(providerId, id, payload)
            : adminProvidersApi.createStaff(providerId, payload);

    const onStaffSaved = async () => {
        const list = await adminProvidersApi.listStaff(providerId);
        setStaff(list);
    };

    const onStaffDelete = async () => {
        try {
            await adminProvidersApi.deleteStaff(providerId, confirmStaffDel.id);
            toast.success(t("provider.staff.deleted"));
            setConfirmStaffDel(null);
            const list = await adminProvidersApi.listStaff(providerId);
            setStaff(list);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };

    const onProviderEdited = (updated) => {
        setProvider(updated);
        onProviderChanged?.(updated);
    };

    // Offers (admin scope) — create-on-behalf is auto-approved by the backend.
    // Editing existing offers is left to the provider; admin can decide PENDING ones inline.
    const refreshOffers = async () => {
        try {
            const res = await adminOffersApi.list({ provider_id: providerId });
            setOffers(res.items || []);
        } catch (_) { /* ignore */ }
    };

    const saveOffer = ({ payload }) =>
        adminOffersApi.createForProvider(providerId, payload);

    const onOfferSaved = () => refreshOffers();

    const decideOffer = async (offer, action, reason) => {
        try {
            const updated = await adminOffersApi.decide(offer.id, {
                decision: action,
                rejection_reason: action === "REJECTED" ? (reason || "").trim() : null,
            });
            toast.success(t(action === "APPROVED" ? "offers.toasts.approved" : "offers.toasts.rejected"));
            setOffers((prev) => prev?.map((o) => o.id === updated.id ? updated : o) || prev);
            setDecision({ open: false, offer: null, action: null });
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };

    // Restrict the offer category picker to this provider's linked specialties.
    const providerCategories = useMemo(() => {
        if (!provider?.category_ids?.length || !allL2Categories.length) return allL2Categories;
        const set = new Set(provider.category_ids);
        return allL2Categories.filter((c) => set.has(c.id));
    }, [provider, allL2Categories]);

    const expSoon = provider?.license_days_to_expiry !== null
        && provider?.license_days_to_expiry !== undefined
        && provider?.license_days_to_expiry <= 30;

    const activeLocations = useMemo(() => (locations || []).filter((l) => l.is_active), [locations]);

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent
                    side={isRTL ? "left" : "right"}
                    className="w-full sm:max-w-3xl overflow-y-auto"
                    data-testid="provider-detail-sheet"
                    dir={isRTL ? "rtl" : "ltr"}
                >
                    <SheetHeader className="text-start">
                        <div className="flex items-start gap-3">
                            <div className="icon-tile h-12 w-12 shrink-0 overflow-hidden">
                                {provider?.logo_url
                                    ? <img src={provider.logo_url} alt="" className="h-full w-full object-cover" />
                                    : <Building2 className="h-6 w-6" />}
                            </div>
                            <div className="min-w-0 flex-1">
                                <SheetTitle className="text-2xl truncate" data-testid="provider-detail-name">
                                    {provider?.corporate_name || "—"}
                                </SheetTitle>
                                <SheetDescription className="sr-only">
                                    {provider?.corporate_name || "Provider"}
                                </SheetDescription>
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                    {provider && <span className="text-xs px-2 py-0.5 rounded-full bg-muted">{t(`provider.types.${provider.type}`)}</span>}
                                    {provider && <KycBadge status={provider.kyc_status} t={t} />}
                                    {provider?.is_visible
                                        ? <Badge variant="outline" className="border-success/40 text-success">{t("admin.providers.visible")}</Badge>
                                        : provider && <Badge variant="secondary">{t("admin.providers.hidden")}</Badge>}
                                </div>
                            </div>
                        </div>
                    </SheetHeader>

                    {loading && !provider ? (
                        <div className="py-20 flex items-center justify-center">
                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        </div>
                    ) : provider ? (
                        <Tabs value={tab} onValueChange={setTab} className="mt-6">
                            <TabsList className="grid grid-cols-4 w-full">
                                <TabsTrigger value="overview" data-testid="provider-tab-overview">{t("admin.providers.tabs.overview")}</TabsTrigger>
                                <TabsTrigger value="locations" data-testid="provider-tab-locations">
                                    {t("admin.providers.tabs.locations")} <span className="ms-1 text-xs opacity-60">({locations?.length ?? "—"})</span>
                                </TabsTrigger>
                                <TabsTrigger value="staff" data-testid="provider-tab-staff">
                                    {t("admin.providers.tabs.staff")} <span className="ms-1 text-xs opacity-60">({staff?.length ?? "—"})</span>
                                </TabsTrigger>
                                <TabsTrigger value="offers" data-testid="provider-tab-offers">
                                    {t("admin.providers.tabs.offers")} <span className="ms-1 text-xs opacity-60">({offers?.length ?? "—"})</span>
                                </TabsTrigger>
                            </TabsList>

                            {/* OVERVIEW */}
                            <TabsContent value="overview" className="space-y-4 mt-4">
                                {expSoon && (
                                    <div className="rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive flex items-center gap-2" data-testid="provider-license-warning">
                                        <AlertTriangle className="h-4 w-4 shrink-0" />
                                        {t("admin.providers.licenseExpiring", { days: provider.license_days_to_expiry })}
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-3">
                                    <div className="rounded-xl border border-border bg-muted/20 px-4 py-3">
                                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("admin.providers.cols.branches")}</p>
                                        <p className="text-2xl font-semibold mt-0.5">{provider.branch_count || 0}</p>
                                    </div>
                                    <div className="rounded-xl border border-border bg-muted/20 px-4 py-3">
                                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("admin.providers.fields.categories")}</p>
                                        <p className="text-2xl font-semibold mt-0.5">{provider.category_ids?.length || 0}</p>
                                    </div>
                                </div>

                                {provider.neighborhoods?.length > 0 && (
                                    <div>
                                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">{t("admin.providers.neighborhoods")}</p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {provider.neighborhoods.map((n) => (
                                                <span key={n} className="px-2 py-1 rounded-full bg-secondary text-xs">{n}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {(provider.description_en || provider.description_ar) && (
                                    <div className="space-y-2">
                                        {provider.description_en && (
                                            <div>
                                                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">{t("admin.providers.fields.descriptionEn")}</p>
                                                <p className="text-sm">{provider.description_en}</p>
                                            </div>
                                        )}
                                        {provider.description_ar && (
                                            <div>
                                                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">{t("admin.providers.fields.descriptionAr")}</p>
                                                <p className="text-sm font-arabic" dir="rtl">{provider.description_ar}</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {(provider.license_number || provider.license_expiry) && (
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        {provider.license_number && (
                                            <div>
                                                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("admin.providers.fields.licenseNumber")}</p>
                                                <p className="mt-0.5 font-mono">{provider.license_number}</p>
                                            </div>
                                        )}
                                        {provider.license_expiry && (
                                            <div>
                                                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("admin.providers.fields.licenseExpiry")}</p>
                                                <p className="mt-0.5">{provider.license_expiry}</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="rounded-xl border border-border bg-muted/20 px-4 py-3">
                                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1">
                                        <Mail className="h-3 w-3" />
                                        {t("admin.providers.adminSection")}
                                    </p>
                                    {provider.admin ? (
                                        <>
                                            <p className="text-sm mt-1">{provider.admin.full_name}</p>
                                            <p className="text-xs text-muted-foreground font-mono">{provider.admin.email}</p>
                                        </>
                                    ) : (
                                        <p className="text-sm mt-1 italic text-muted-foreground">{t("admin.providers.noAdmin")}</p>
                                    )}
                                </div>

                                <Button
                                    variant="outline"
                                    onClick={() => setEditOpen(true)}
                                    data-testid="provider-detail-edit"
                                >
                                    <Pencil className="h-4 w-4 me-1" />
                                    {t("admin.providers.editTitle")}
                                </Button>
                            </TabsContent>

                            {/* LOCATIONS */}
                            <TabsContent value="locations" className="space-y-3 mt-4">
                                <div className="flex justify-between items-center">
                                    <p className="text-sm text-muted-foreground">{t("admin.providers.locations.subtitle")}</p>
                                    <Button size="sm" onClick={() => setLocDialog({ open: true, initial: null })} data-testid="admin-add-location-button">
                                        <Plus className="h-4 w-4 me-1" />
                                        {t("provider.locations.add")}
                                    </Button>
                                </div>

                                {locations === null ? (
                                    <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
                                ) : locations.length === 0 ? (
                                    <div className="surface-card p-10 text-center" data-testid="admin-locations-empty">
                                        <Inbox className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
                                        <p className="text-sm font-medium">{t("provider.locations.empty")}</p>
                                        <p className="mt-1 text-xs text-muted-foreground">{t("provider.locations.emptyHint")}</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {locations.map((loc) => (
                                            <div key={loc.id} className="surface-card p-4 group" data-testid={`admin-location-card-${loc.id}`}>
                                                <div className="flex items-start gap-3">
                                                    <div className="icon-tile h-9 w-9 shrink-0">
                                                        <MapPin className="h-4 w-4" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <h4 className="text-sm font-semibold truncate">{loc.name_en}</h4>
                                                            {!loc.is_active && <Badge variant="secondary" className="text-[10px]">{t("provider.locations.inactive")}</Badge>}
                                                        </div>
                                                        <p className="mt-0.5 text-xs text-muted-foreground truncate font-arabic" dir="rtl">{loc.name_ar}</p>
                                                        <p className="mt-1 text-xs text-muted-foreground">{[loc.neighborhood, loc.city].filter(Boolean).join(", ") || "—"}</p>
                                                        {loc.phone && <p className="text-xs text-muted-foreground" dir="ltr">{loc.phone}</p>}
                                                        {loc.hours_text && <p className="mt-0.5 text-xs text-muted-foreground">{loc.hours_text}</p>}
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <Button size="icon" variant="ghost" onClick={() => setLocDialog({ open: true, initial: loc })} data-testid={`admin-location-edit-${loc.id}`}>
                                                            <Pencil className="h-4 w-4" />
                                                        </Button>
                                                        <Button size="icon" variant="ghost" onClick={() => setConfirmLocDel(loc)} data-testid={`admin-location-delete-${loc.id}`}>
                                                            <Trash2 className="h-4 w-4 text-destructive" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </TabsContent>

                            {/* STAFF */}
                            <TabsContent value="staff" className="space-y-3 mt-4">
                                <div className="flex justify-between items-center">
                                    <p className="text-sm text-muted-foreground">{t("admin.providers.staff.subtitle")}</p>
                                    <Button
                                        size="sm"
                                        disabled={activeLocations.length === 0}
                                        onClick={() => setStaffDialog({ open: true, initial: null })}
                                        data-testid="admin-add-staff-button"
                                    >
                                        <Plus className="h-4 w-4 me-1" />
                                        {t("provider.staff.add")}
                                    </Button>
                                </div>

                                {activeLocations.length === 0 && (
                                    <div className="rounded-xl border border-warning/40 bg-warning/5 px-3 py-2 text-xs">
                                        {t("provider.staff.noLocationsHint")}
                                    </div>
                                )}

                                {staff === null ? (
                                    <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
                                ) : staff.length === 0 ? (
                                    <div className="surface-card p-10 text-center" data-testid="admin-staff-empty">
                                        <Inbox className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
                                        <p className="text-sm font-medium">{t("provider.staff.empty")}</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {staff.map((s) => (
                                            <div key={s.id} className="surface-card p-4 group" data-testid={`admin-staff-card-${s.id}`}>
                                                <div className="flex items-start gap-3">
                                                    <div className="icon-tile h-10 w-10 shrink-0">
                                                        <Stethoscope className="h-5 w-5" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="text-sm font-semibold truncate">
                                                            {lang === "ar" ? s.full_name_ar : s.full_name_en}
                                                        </h4>
                                                        <p className="text-xs text-muted-foreground truncate">
                                                            {lang === "ar" ? (s.role_ar || s.role_en) : (s.role_en || s.role_ar)}
                                                        </p>
                                                        {s.license_number && <p className="text-[11px] text-muted-foreground">Lic. {s.license_number}</p>}
                                                        <div className="mt-1.5 flex flex-wrap gap-1">
                                                            {(s.availability || []).map((slot, i) => {
                                                                const loc = locations?.find((l) => l.id === slot.location_id);
                                                                if (!loc) return null;
                                                                return (
                                                                    <Badge key={i} variant="secondary" className="text-[10px]">
                                                                        {loc.name_en} · {(slot.days || []).join("/")} · {slot.from_time}–{slot.to_time}
                                                                    </Badge>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <Button size="icon" variant="ghost" onClick={() => setStaffDialog({ open: true, initial: s })} data-testid={`admin-staff-edit-${s.id}`}>
                                                            <Pencil className="h-4 w-4" />
                                                        </Button>
                                                        <Button size="icon" variant="ghost" onClick={() => setConfirmStaffDel(s)} data-testid={`admin-staff-delete-${s.id}`}>
                                                            <Trash2 className="h-4 w-4 text-destructive" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </TabsContent>

                            {/* OFFERS */}
                            <TabsContent value="offers" className="space-y-3 mt-4">
                                <div className="flex justify-between items-center">
                                    <p className="text-sm text-muted-foreground">{t("admin.providers.offers.subtitle")}</p>
                                    <Button
                                        size="sm"
                                        onClick={() => setOfferDialog({ open: true, initial: null })}
                                        data-testid="admin-add-offer-button"
                                    >
                                        <Plus className="h-4 w-4 me-1" />
                                        {t("offers.add")}
                                    </Button>
                                </div>

                                {offers === null ? (
                                    <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
                                ) : offers.length === 0 ? (
                                    <div className="surface-card p-10 text-center" data-testid="admin-offers-empty">
                                        <Inbox className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
                                        <p className="text-sm font-medium">{t("offers.empty")}</p>
                                        <p className="mt-1 text-xs text-muted-foreground">{t("admin.providers.offers.emptyHint")}</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {offers.map((offer) => {
                                            const titleText = lang === "ar" ? (offer.title_ar || offer.title_en) : (offer.title_en || offer.title_ar);
                                            const statusTone = offer.status === "APPROVED" ? "border-success/40 text-success bg-success/5"
                                                            : offer.status === "PENDING"  ? "border-warning/40 text-warning bg-warning/5"
                                                            : offer.status === "REJECTED" ? "border-destructive/40 text-destructive bg-destructive/5"
                                                            : "border-border text-muted-foreground";
                                            return (
                                                <div key={offer.id} className="surface-card p-4" data-testid={`admin-offer-card-${offer.id}`}>
                                                    <div className="flex items-start gap-3">
                                                        <div className="icon-tile h-10 w-10 shrink-0">
                                                            <Sparkles className="h-5 w-5" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-start gap-2 flex-wrap">
                                                                <h4 className="text-sm font-semibold truncate flex-1 min-w-0">{titleText}</h4>
                                                                <Badge variant="outline" className={statusTone}>{t(`offers.status.${offer.status}`)}</Badge>
                                                                {offer.is_live && (
                                                                    <Badge variant="outline" className="border-success/40 text-success text-[10px]">{t("offers.live")}</Badge>
                                                                )}
                                                            </div>
                                                            <div className="mt-1.5 flex items-baseline gap-2 text-xs text-muted-foreground" dir="ltr">
                                                                {offer.discount_type === "PERCENTAGE" ? (
                                                                    <span className="text-base font-bold text-primary">{Math.round(offer.discount_value)}%</span>
                                                                ) : (
                                                                    <span className="text-base font-bold text-primary">{Math.round(offer.discount_value)} {t("offers.egp")}</span>
                                                                )}
                                                                <span>· {offer.start_date} → {offer.end_date}</span>
                                                            </div>
                                                            {offer.rejection_reason && (
                                                                <div className="mt-2 rounded-lg border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-xs text-destructive">
                                                                    <strong>{t("offers.rejectionReason")}: </strong>{offer.rejection_reason}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {offer.status === "PENDING" && (
                                                            <div className="flex flex-col gap-1 shrink-0">
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => setDecision({ open: true, offer, action: "APPROVED" })}
                                                                    data-testid={`admin-offer-approve-${offer.id}`}
                                                                >
                                                                    <CheckCircle2 className="h-4 w-4 me-1" />
                                                                    {t("offers.approveAction")}
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => setDecision({ open: true, offer, action: "REJECTED" })}
                                                                    className="text-destructive hover:bg-destructive/5"
                                                                    data-testid={`admin-offer-reject-${offer.id}`}
                                                                >
                                                                    <XCircle className="h-4 w-4 me-1" />
                                                                    {t("offers.rejectAction")}
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </TabsContent>
                        </Tabs>
                    ) : null}
                </SheetContent>
            </Sheet>

            {/* Provider edit (overview tab) */}
            <EditProviderDialog
                open={editOpen}
                onOpenChange={setEditOpen}
                provider={provider}
                onSaved={onProviderEdited}
            />

            {/* Location dialogs */}
            <LocationFormDialog
                open={locDialog.open}
                onOpenChange={(o) => setLocDialog((d) => ({ ...d, open: o }))}
                initial={locDialog.initial}
                save={saveLocation}
                onSaved={onLocationSaved}
            />
            <AlertDialog open={!!confirmLocDel} onOpenChange={(o) => !o && setConfirmLocDel(null)}>
                <AlertDialogContent data-testid="admin-location-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("provider.locations.confirmDeleteTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("provider.locations.confirmDeleteBody", { name: confirmLocDel?.name_en || "" })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={onLocationDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="admin-location-delete-confirm-button">
                            {t("provider.locations.delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Staff dialogs */}
            <StaffFormDialog
                open={staffDialog.open}
                onOpenChange={(o) => setStaffDialog((d) => ({ ...d, open: o }))}
                initial={staffDialog.initial}
                locations={activeLocations}
                save={saveStaff}
                onSaved={onStaffSaved}
            />
            <AlertDialog open={!!confirmStaffDel} onOpenChange={(o) => !o && setConfirmStaffDel(null)}>
                <AlertDialogContent data-testid="admin-staff-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("provider.staff.confirmDeleteTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("provider.staff.confirmDeleteBody", { name: confirmStaffDel?.full_name_en || "" })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={onStaffDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="admin-staff-delete-confirm-button">
                            {t("provider.staff.delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Offer create dialog (admin → auto-approved on save) */}
            <OfferFormDialog
                open={offerDialog.open}
                onOpenChange={(o) => setOfferDialog((d) => ({ ...d, open: o }))}
                initial={offerDialog.initial}
                save={saveOffer}
                onSaved={onOfferSaved}
                locations={activeLocations}
                staff={(staff || []).filter((s) => s.is_active)}
                categories={providerCategories}
            />

            {/* Decision dialog (approve / reject pending offers) */}
            <DecisionDialog
                open={decision.open}
                onOpenChange={(o) => setDecision((d) => ({ ...d, open: o }))}
                offer={decision.offer}
                action={decision.action}
                onConfirm={(reason) => decideOffer(decision.offer, decision.action, reason)}
                t={t}
                isRTL={isRTL}
            />
        </>
    );
}

function DecisionDialog({ open, onOpenChange, offer, action, onConfirm, t, isRTL }) {
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { if (open) { setReason(""); setSubmitting(false); } }, [open]);

    if (!offer) return null;
    const isReject = action === "REJECTED";

    const handle = async (e) => {
        e?.preventDefault();
        setSubmitting(true);
        try { await onConfirm(reason); } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-md rounded-2xl"
                data-testid={isReject ? "admin-offer-reject-dialog" : "admin-offer-approve-dialog"}
                dir={isRTL ? "rtl" : "ltr"}
            >
                <DialogHeader>
                    <DialogTitle>{t(isReject ? "offers.rejectTitle" : "offers.approveTitle")}</DialogTitle>
                    <DialogDescription>{offer.title_en}</DialogDescription>
                </DialogHeader>
                <form onSubmit={handle} className="space-y-3 mt-2">
                    {isReject ? (
                        <div>
                            <Label htmlFor="reject_reason">{t("offers.fields.rejectionReason")}</Label>
                            <Textarea
                                id="reject_reason"
                                rows={4}
                                required
                                placeholder={t("offers.fields.rejectionReasonPlaceholder")}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                className="mt-1.5"
                                data-testid="admin-offer-rejection-reason"
                            />
                        </div>
                    ) : (
                        <p className="text-sm text-muted-foreground">{t("offers.approveBody")}</p>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
                        <Button
                            type="submit"
                            disabled={submitting || (isReject && !reason.trim())}
                            className={isReject ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
                            data-testid={isReject ? "admin-offer-reject-confirm" : "admin-offer-approve-confirm"}
                        >
                            {submitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                            {t(isReject ? "offers.rejectAction" : "offers.approveAction")}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

